import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import ModuleLayout from '../common/ModuleLayout';

const { FiEdit } = FiIcons;

const ModuleFive = ({ data, onSave, completed }) => {
  const [bookType, setBookType] = useState('nonfiction');
  const [fictionData, setFictionData] = useState({
    mainCharacter: '',
    openingSetting: '',
    sensoryDetails: [],
    initialTension: '',
    openingParagraph: ''
  });
  const [nonfictionData, setNonfictionData] = useState({
    hook: '',
    look: '',
    took: '',
    openingParagraph: ''
  });
  const [commitment, setCommitment] = useState({
    date: '',
    time: '',
    location: '',
    committed: false
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (data.bookType) setBookType(data.bookType);
    if (data.fictionData) setFictionData(data.fictionData);
    if (data.nonfictionData) setNonfictionData(data.nonfictionData);
    if (data.commitment) setCommitment(data.commitment);
  }, [data]);

  const handleFictionChange = (field, value) => {
    setFictionData(prev => ({ ...prev, [field]: value }));
  };

  const handleNonfictionChange = (field, value) => {
    setNonfictionData(prev => ({ ...prev, [field]: value }));
  };

  const handleCommitmentChange = (field, value) => {
    setCommitment(prev => ({ ...prev, [field]: value }));
  };

  const handleSensoryDetailAdd = (detail) => {
    if (detail.trim() && !fictionData.sensoryDetails.includes(detail.trim())) {
      setFictionData(prev => ({
        ...prev,
        sensoryDetails: [...prev.sensoryDetails, detail.trim()]
      }));
    }
  };

  const handleSensoryDetailRemove = (index) => {
    setFictionData(prev => ({
      ...prev,
      sensoryDetails: prev.sensoryDetails.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async (markComplete) => {
    setIsLoading(true);
    const dataToSave = {
      bookType,
      fictionData,
      nonfictionData,
      commitment
    };
    
    setTimeout(() => {
      onSave(dataToSave, markComplete);
      setIsLoading(false);
    }, 500);
  };

  const canComplete = bookType === 'fiction'
    ? fictionData.openingParagraph.trim() !== '' && commitment.date && commitment.time && commitment.location && commitment.committed
    : nonfictionData.openingParagraph.trim() !== '' && commitment.date && commitment.time && commitment.location && commitment.committed;

  return (
    <ModuleLayout
      title="Kickstart the First Chapter"
      description="Break the blank page and begin writing today."
      icon={FiEdit}
      onSave={handleSave}
      completed={completed}
      canComplete={canComplete}
      isLoading={isLoading}
    >
      <div className="space-y-8">
        {/* Book Type Selector */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Book Type</h3>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="fiction"
                checked={bookType === 'fiction'}
                onChange={(e) => setBookType(e.target.value)}
                className="mr-2"
              />
              <span className="text-gray-700">Fiction</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="nonfiction"
                checked={bookType === 'nonfiction'}
                onChange={(e) => setBookType(e.target.value)}
                className="mr-2"
              />
              <span className="text-gray-700">Nonfiction</span>
            </label>
          </div>
        </div>

        {/* Fiction Fields */}
        {bookType === 'fiction' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Fiction Elements</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Main Character
                  </label>
                  <input
                    type="text"
                    value={fictionData.mainCharacter}
                    onChange={(e) => handleFictionChange('mainCharacter', e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Who is your protagonist?"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opening Setting
                  </label>
                  <input
                    type="text"
                    value={fictionData.openingSetting}
                    onChange={(e) => handleFictionChange('openingSetting', e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Where does your story begin?"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sensory Details
                  </label>
                  <div className="space-y-2">
                    <input
                      type="text"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSensoryDetailAdd(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Add sensory details (press Enter to add)"
                    />
                    <div className="flex flex-wrap gap-2">
                      {fictionData.sensoryDetails.map((detail, index) => (
                        <span
                          key={index}
                          className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center"
                        >
                          {detail}
                          <button
                            onClick={() => handleSensoryDetailRemove(index)}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Initial Tension
                  </label>
                  <textarea
                    value={fictionData.initialTension}
                    onChange={(e) => handleFictionChange('initialTension', e.target.value)}
                    rows={3}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="What conflict or question draws readers in?"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                Opening Paragraph Draft
              </label>
              <textarea
                value={fictionData.openingParagraph}
                onChange={(e) => handleFictionChange('openingParagraph', e.target.value)}
                rows={6}
                className="w-full p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Write your opening paragraph here. Don't worry about perfection—just begin!"
              />
            </div>
          </div>
        )}

        {/* Nonfiction Fields */}
        {bookType === 'nonfiction' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Nonfiction Structure</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hook (Problem or Desire)
                  </label>
                  <textarea
                    value={nonfictionData.hook}
                    onChange={(e) => handleNonfictionChange('hook', e.target.value)}
                    rows={3}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="What problem or desire will grab your reader's attention?"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Look (Truth or Insight)
                  </label>
                  <textarea
                    value={nonfictionData.look}
                    onChange={(e) => handleNonfictionChange('look', e.target.value)}
                    rows={3}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="What truth or insight will you share?"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Took (Application)
                  </label>
                  <textarea
                    value={nonfictionData.took}
                    onChange={(e) => handleNonfictionChange('took', e.target.value)}
                    rows={3}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="How can readers apply this in their lives?"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                Opening Paragraph Draft
              </label>
              <textarea
                value={nonfictionData.openingParagraph}
                onChange={(e) => handleNonfictionChange('openingParagraph', e.target.value)}
                rows={6}
                className="w-full p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Write your opening paragraph here. Start with your hook and draw readers in!"
              />
            </div>
          </div>
        )}

        {/* Writing Sprint Commitment */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Writing Sprint Commitment</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={commitment.date}
                  onChange={(e) => handleCommitmentChange('date', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time
                </label>
                <input
                  type="time"
                  value={commitment.time}
                  onChange={(e) => handleCommitmentChange('time', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={commitment.location}
                  onChange={(e) => handleCommitmentChange('location', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Where will you write?"
                />
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-blue-50 to-orange-50 rounded-lg p-4 border border-blue-200">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={commitment.committed}
                  onChange={(e) => handleCommitmentChange('committed', e.target.checked)}
                  className="mt-1 mr-3 text-blue-500 focus:ring-blue-500"
                />
                <span className="text-gray-800 font-medium">
                  I commit to starting before I feel ready.
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Encouragement */}
        {canComplete && (
          <div className="bg-gradient-to-r from-blue-600 to-orange-600 rounded-xl p-6 text-center text-white">
            <h3 className="text-xl font-bold mb-3">You're Ready to Begin!</h3>
            <p className="mb-4">
              Your opening paragraph is drafted and your writing commitment is set. 
              Remember: faithful progress matters more than perfect consistency.
            </p>
            <p className="text-blue-100 italic">
              "For I know the plans I have for you," declares the Lord, "plans to prosper you and not to harm you, to give you hope and a future." - Jeremiah 29:11
            </p>
          </div>
        )}
      </div>
    </ModuleLayout>
  );
};

export default ModuleFive;